import type { ConsultationResponseDto, ConsultationWaitlistDto } from '@montugroup/pms-api-contracts';
import { useRef, useState } from 'react';

import { useConsultationWaitlistTracking } from '@/hooks/analytics/useConsultationWaitlistTracking';
import { useLeaveConsultationWaitlist } from '@/hooks/consultations/useLeaveConsultationWaitlist';
import { TextButton } from './ConsultationWaitlistSection.styles';
import { LeaveWaitlistDialog } from './LeaveWaitlistDialog';
import { WAITLIST_GENERIC_ERROR_MESSAGE } from './waitlist.config';

export interface LeaveWaitlistButtonProps {
  consultation: Pick<ConsultationResponseDto, 'id' | 'code'>;
  availability: ConsultationWaitlistDto['availability'];
  /** called once the patient has left; the section announces it */
  onLeft: () => void;
}

/**
 * Leave waitlist, with the dialog that confirms it.
 */
export function LeaveWaitlistButton({ consultation, availability, onLeft }: LeaveWaitlistButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // the dialog is an overlay, so this button stays mounted and gets focus back when it closes
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { leave, isPending } = useLeaveConsultationWaitlist();
  const { trackWaitlistLeft } = useConsultationWaitlistTracking();

  const close = () => {
    setIsOpen(false);
    setErrorMessage(null);
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await leave(consultation.code);
      trackWaitlistLeft(consultation.id, availability);
      close();
      onLeft();
    } catch {
      setErrorMessage(WAITLIST_GENERIC_ERROR_MESSAGE);
    }
  };

  const handleCancel = () => {
    close();
    requestAnimationFrame(() => buttonRef.current?.focus());
  };

  return (
    <>
      <TextButton type="button" ref={buttonRef} onClick={() => setIsOpen(true)}>
        Leave waitlist
      </TextButton>
      <LeaveWaitlistDialog
        open={isOpen}
        isLeaving={isPending}
        errorMessage={errorMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
